import { Fragment, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { State } from 'redux/initialState';
import { useTranslate } from 'hooks/useTranslate';

import isSuperAdmin from 'utils/isSuperAdmin';

import {
  FEATURE_GROUPS,
  FeatureGroup,
  SwitchFeature,
} from 'modules/society/constants/societyFeatures';
import { updateSocietyConfiguration } from 'modules/society/redux/societyActions';

const SocietyFeatures = () => {
  const { t } = useTranslate();
  const dispatch = useDispatch();

  const user = useSelector((state: State) => state.user);
  const actualSociety = useSelector(
    (state: State) => state.society?.actualSociety
  );
  const config = actualSociety?.configuration;

  const [loadingKey, setLoadingKey] = useState<string | null>(null);
  const [localDecimalScale, setLocalDecimalScale] = useState<number>(
    config?.decimalScale ?? 2
  );
  const [savingDecimal, setSavingDecimal] = useState(false);

  useEffect(() => {
    if (config?.decimalScale !== undefined) {
      setLocalDecimalScale(config.decimalScale);
    }
  }, [config?.decimalScale]);

  if (!isSuperAdmin(user)) return null;

  const handleToggleFeature = async (configKey: string, newValue: boolean) => {
    if (loadingKey !== null || !actualSociety) return;
    setLoadingKey(configKey);
    try {
      await dispatch(
        updateSocietyConfiguration(
          actualSociety._id,
          { [configKey]: newValue },
          false
        )
      );
    } finally {
      setLoadingKey(null);
    }
  };

  const handleSaveDecimalScale = async () => {
    if (savingDecimal || !actualSociety) return;
    setSavingDecimal(true);
    try {
      await dispatch(
        updateSocietyConfiguration(
          actualSociety._id,
          { decimalScale: localDecimalScale },
          false
        )
      );
    } finally {
      setSavingDecimal(false);
    }
  };

  const formatDecimalPreview = (scale: number): string =>
    (12345.6789012).toLocaleString('es-ES', {
      minimumFractionDigits: scale,
      maximumFractionDigits: scale,
    });

  const countActiveSwitches = (
    group: FeatureGroup
  ): { active: number; total: number } => {
    const switches = group.features.filter(
      (f): f is SwitchFeature => f.type === 'switch'
    );
    const active = switches.filter((f) => f.getValue(config)).length;
    return { active, total: switches.length };
  };

  return (
    <div className="nk-block">
      <div className="nk-block-head">
        <div className="nk-block-head-content">
          <h5 className="nk-block-title title">{t('SocietyFeatures')}</h5>
          <div className="nk-block-des">
            <p>{t('SocietyFeaturesDescription')}</p>
          </div>
        </div>
      </div>

      <div className="card card-bordered card-preview">
        <table className="table">
          <tbody>
            {FEATURE_GROUPS.map((group) => {
              const { active, total } = countActiveSwitches(group);
              return (
                <Fragment key={group.key}>
                  <tr className="nk-tb-item tb-tnx-head">
                    <td colSpan={2} className="nk-tb-col py-1">
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center">
                          <div
                            className={`d-flex align-items-center justify-content-center rounded-circle bg-${group.color}-dim mr-2`}
                            style={{ width: 28, height: 28, minWidth: 28 }}
                          >
                            <em
                              className={`icon ni ${group.icon} text-${group.color} fs-14px`}
                            />
                          </div>
                          <span className="fw-bold">{t(group.labelKey)}</span>
                        </div>
                        <span
                          className={`badge badge-dim badge-${group.color}`}
                        >
                          {active} / {total} {t('SocietyFeaturesActive')}
                        </span>
                      </div>
                    </td>
                  </tr>

                  {group.features.map((feature) => (
                    <tr key={feature.configKey} className="nk-tb-item">
                      <td className="nk-tb-col">
                        <div className="d-flex align-items-center flex-wrap">
                          <span className="fw-medium">
                            {t(feature.labelKey)}
                          </span>
                          <span
                            className="badge badge-dim badge-light text-monospace fw-normal fs-11px ml-2"
                            title={feature.configKey}
                            data-testid={`feature-config-key-${feature.configKey}`}
                          >
                            {feature.configKey}
                          </span>
                        </div>
                        <div className="fs-12px text-soft">
                          {t(feature.descKey)}
                        </div>
                      </td>

                      <td
                        className="nk-tb-col text-right vertical-align-middle"
                        style={{ width: 280 }}
                      >
                        {feature.type === 'number' ? (
                          <div className="d-flex align-items-center justify-content-end mr-1">
                            <span className="text-soft fs-12px mr-1">
                              {t('SocietyFeature_DecimalPreview')}
                            </span>
                            <code className="text-dark mr-3">
                              {formatDecimalPreview(localDecimalScale)}
                            </code>
                            <div
                              className="input-group input-group-sm"
                              style={{ width: 72 }}
                            >
                              <input
                                type="number"
                                className="form-control text-center"
                                value={localDecimalScale}
                                min={feature.min}
                                max={feature.max}
                                onChange={(e) =>
                                  setLocalDecimalScale(
                                    Math.min(
                                      feature.max,
                                      Math.max(
                                        feature.min,
                                        Number(e.target.value)
                                      )
                                    )
                                  )
                                }
                                disabled={savingDecimal}
                                data-testid="decimal-scale-input"
                              />
                            </div>
                            <div className="input-group-append">
                              <button
                                type="button"
                                className="btn btn-sm btn-icon btn-primary"
                                onClick={handleSaveDecimalScale}
                                disabled={savingDecimal}
                                title={t('Save')}
                                data-testid="decimal-scale-save"
                              >
                                {savingDecimal ? (
                                  <span
                                    className="spinner-border spinner-border-sm"
                                    role="status"
                                  />
                                ) : (
                                  <em className="icon ni ni-save" />
                                )}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="d-flex align-items-center justify-content-end">
                            {loadingKey === feature.configKey && (
                              <span
                                className="spinner-border spinner-border-sm text-primary mr-2"
                                role="status"
                              />
                            )}
                            <div className="custom-control custom-control-sm custom-switch">
                              <input
                                type="checkbox"
                                className="custom-control-input"
                                id={`feature-${feature.configKey}`}
                                checked={(feature as SwitchFeature).getValue(
                                  config
                                )}
                                onChange={(e) =>
                                  handleToggleFeature(
                                    feature.configKey,
                                    e.target.checked
                                  )
                                }
                                disabled={loadingKey !== null}
                                data-testid={`feature-toggle-${feature.configKey}`}
                              />
                              <label
                                className="custom-control-label"
                                htmlFor={`feature-${feature.configKey}`}
                              />
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SocietyFeatures;
